import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useCampaignQuery } from "modules/campaigns/hooks/useCampaignQuery"
import { getCampaignQueryKey } from "modules/campaigns/lib/campaignQuery"
import { EMPTY_AUDIENCE_RULES } from "modules/campaigns/lib/launchAudienceRules"
import { ImportFlow, ImportStartProps } from "modules/customers/components/Import/ImportFlow"
import { useState } from "react"
import { CampaignLaunchAudience, CampaignTriggerTypeEnum } from "schema"
import { AudienceChoice, LaunchAudienceStep } from "./LaunchAudienceStep"
import { ManualLaunchReach } from "./ManualLaunchReach"

type Props = {
  onClose: () => void
  campaignId: string
  importId?: string
}

export function ManualLaunch({ onClose, campaignId, importId }: Props) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: campaign } = useCampaignQuery(campaignId)

  const [choice, setChoice] = useState<AudienceChoice>("rules")
  const [rules, setRules] = useState(EMPTY_AUDIENCE_RULES)
  const [rulesAudience, setRulesAudience] = useState<CampaignLaunchAudience>()

  if (!campaign) return null

  const isManualCampaign = campaign.trigger === CampaignTriggerTypeEnum.Manual

  function changeImportId(next: string | undefined) {
    if (next) queryClient.invalidateQueries({ queryKey: getCampaignQueryKey(campaignId) })

    navigate({
      to: "/campaigns/$id/insights/launch",
      params: { id: campaignId },
      search: next ? { importId: next } : {},
      replace: true,
    })
  }

  function renderAudienceStep(startProps: ImportStartProps) {
    if (rulesAudience) {
      return (
        <ManualLaunchReach
          campaignId={campaignId}
          audienceSource={rulesAudience}
          onClose={onClose}
          onChangeAudience={() => setRulesAudience(undefined)}
        />
      )
    }

    return (
      <LaunchAudienceStep
        {...startProps}
        campaignId={campaignId}
        pendingImportId={campaign?.pendingImports?.at(0)?.id}
        choice={choice}
        rules={rules}
        onChoiceChange={setChoice}
        onRulesChange={setRules}
        onUseImport={changeImportId}
        onContinueWithRules={setRulesAudience}
        onClose={onClose}
      />
    )
  }

  return (
    <ImportFlow
      importId={importId}
      campaignId={campaignId}
      className={isManualCampaign && !importId && !rulesAudience ? "sm:max-w-2xl" : undefined}
      onClose={onClose}
      onImportIdChange={changeImportId}
      renderStart={isManualCampaign ? renderAudienceStep : undefined}
      renderComplete={(_state, completedImportId) => (
        <ManualLaunchReach
          campaignId={campaignId}
          audienceSource={{ kind: "import", importId: completedImportId }}
          onClose={onClose}
          onChangeAudience={() => changeImportId(undefined)}
        />
      )}
    />
  )
}
